export function setJsonLd(id: string, data: Record<string, unknown>): void {
  // Find existing script by ID (handles both server-rendered and client-created)
  let script = document.getElementById(id) as HTMLScriptElement | null;
  
  if (!script) {
    // Check for duplicate JSON-LD scripts with same @type but no id (legacy cleanup)
    const schemaType = data["@type"] as string | undefined;
    if (schemaType) {
      const existingScripts = document.querySelectorAll('script[type="application/ld+json"]:not([id])');
      existingScripts.forEach(s => {
        try {
          const content = JSON.parse(s.textContent || "{}");
          if (content["@type"] === schemaType) {
            s.remove();
          }
        } catch { /* ignore parse errors */ }
      });
    }
    
    script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    document.head.appendChild(script);
  }
  
  script.textContent = JSON.stringify(data);
}

// Keep identical to server/lib/marketingMeta.ts organizationSchema, the
// client re-sets this on hydration, so both must carry the same entity data.
export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Bitzo",
  "url": "https://bitzo.net",
  "foundingDate": "2026",
  "logo": "https://bitzo.net/web-app-manifest-512x512.png",
  "description": "UK-based non-custodial crypto security and inheritance coordination. Verified contacts, recovery workflows, full audit trails. No seed phrases.",
  "areaServed": "GB",
  "sameAs": [
    "https://www.linkedin.com/company/bitzo1",
    "https://github.com/bitzo-crypto",
    "https://www.youtube.com/@Bitzo-crypto",
    "https://x.com/Bitzocrypto",
    "https://www.crunchbase.com/organization/bitzo"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "contactType": "customer support",
    "url": "https://bitzo.net/contact",
    "areaServed": "GB",
    "availableLanguage": "en"
  },
  "parentOrganization": {
    "@type": "Organization",
    "name": "Raplin Ventures Ltd",
    "legalName": "RAPLIN VENTURES LTD",
    "identifier": "17276917",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "71-75 Shelton Street, Covent Garden",
      "addressLocality": "London",
      "postalCode": "WC2H 9JQ",
      "addressCountry": "GB"
    }
  }
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Bitzo",
  "url": "https://bitzo.net"
};

export function buildFaqSchema(faqs: Array<{ question: string; answer: string }>): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}

export function buildArticleSchema(options: {
  headline: string;
  description: string;
  canonicalUrl: string;
  dateModified?: string;
  datePublished?: string;
  author?: { name: string; role?: string; url?: string };
}): Record<string, unknown> {
  const today = options.dateModified || new Date().toISOString().split("T")[0];
  const author = options.author
    ? {
        "@type": "Person",
        "name": options.author.name, ...(options.author.role ? { "jobTitle": options.author.role } : {}), ...(options.author.url
          ? { "url": options.author.url, "worksFor": { "@type": "Organization", "name": options.author.role || options.author.name, "url": options.author.url } }
          : {}),
      }
    : {
        "@type": "Person",
        "name": "Simon Bumford",
        "jobTitle": "Founder",
        "url": "https://bitzo.net/about",
        "worksFor": { "@type": "Organization", "name": "Bitzo", "url": "https://bitzo.net" }
      };
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": options.headline,
    "description": options.description,
    "datePublished": options.datePublished || today,
    "author": author,
    "publisher": {
      "@type": "Organization",
      "name": "Bitzo",
      "url": "https://bitzo.net",
      "logo": {
        "@type": "ImageObject",
        "url": "https://bitzo.net/web-app-manifest-512x512.png"
      }
    },
    "dateModified": today,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": options.canonicalUrl
    }
  };
}
